import {
    getUnreadCount,
    listNotifications,
    markAllRead,
    markRead,
    type NotificationItem,
} from "@/api/notifications";
import { useClickOutside } from "@/hooks/useClickOutside";
import { useFaviconBadge } from "@/hooks/useFaviconBadge";
import { timeAgo } from "@/utils/timeAgo";
import { Bell, CheckCircle } from "@phosphor-icons/react";
import { useCallback, useEffect, useRef, useState } from "react";
import { Link } from "react-router-dom";
import { ConfirmDialog } from "./ui/ConfirmDialog";

export function NotificationBell() {
  const [unread, setUnread] = useState(0);
  const [open, setOpen] = useState(false);
  useFaviconBadge(unread);
  const [items, setItems] = useState<NotificationItem[]>([]);
  const [loading, setLoading] = useState(false);
  const [confirmOpen, setConfirmOpen] = useState(false);
  const ref = useRef<HTMLDivElement>(null);

  // Poll unread count every 30 s
  useEffect(() => {
    const poll = () =>
      void getUnreadCount()
        .then(setUnread)
        .catch(() => {});
    poll();
    const id = setInterval(poll, 30_000);
    return () => clearInterval(id);
  }, []);

  // Close on outside click
  useClickOutside(ref, () => setOpen(false));

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const data = await listNotifications(1);
      setItems(data.notifications.slice(0, 10));
    } catch {
      /* ignore */
    } finally {
      setLoading(false);
    }
  }, []);

  const toggle = () => {
    const next = !open;
    setOpen(next);
    if (next) void load();
  };

  const handleMarkAll = async () => {
    await markAllRead();
    setUnread(0);
    setItems((prev) => prev.map((n) => ({ ...n, seen: 1 })));
    setConfirmOpen(false);
  };

  const handleMarkOne = async (id: number) => {
    await markRead(id);
    setUnread((c) => Math.max(0, c - 1));
    setItems((prev) => prev.map((n) => (n.id === id ? { ...n, seen: 1 } : n)));
  };

  return (
    <div ref={ref} className="relative">
      <button
        onClick={toggle}
        className="relative p-2 rounded-lg text-text-muted transition-colors hover:bg-bg-surface"
        aria-label="Notifications"
      >
        <Bell size={18} />
        {unread > 0 && (
          <span className="absolute -top-0.5 -right-0.5 flex h-4 min-w-4 items-center justify-center rounded-full bg-red-500 px-1 text-[10px] font-bold text-white">
            {unread > 99 ? "99+" : unread}
          </span>
        )}
      </button>

      {open && (
        <div className="absolute right-0 top-full mt-2 w-80 rounded-xl border border-border bg-bg-card shadow-2xl z-50 overflow-hidden">
          {/* Header */}
          <div className="flex items-center justify-between px-4 py-3 border-b border-border">
            <span className="text-sm font-semibold text-text-primary">
              Notifications
            </span>
            {unread > 0 && (
              <button
                onClick={() => setConfirmOpen(true)}
                className="flex items-center gap-1 text-xs text-primary hover:underline"
              >
                <CheckCircle size={14} />
                Mark all read
              </button>
            )}
          </div>

          {/* List */}
          <div className="max-h-72 overflow-y-auto divide-y divide-border">
            {loading && (
              <p className="py-6 text-center text-sm text-text-muted">
                Loading…
              </p>
            )}
            {!loading && items.length === 0 && (
              <p className="py-6 text-center text-sm text-text-muted">
                No notifications yet
              </p>
            )}
            {!loading &&
              items.map((n) => (
                <button
                  key={n.id}
                  onClick={() => void handleMarkOne(n.id)}
                  className={`w-full text-left px-4 py-3 text-sm transition-colors hover:bg-bg-surface ${
                    n.seen ? "opacity-60" : ""
                  }`}
                >
                  <p className="text-text-primary line-clamp-2">{n.message}</p>
                  {n.submission_title && (
                    <p className="text-xs text-text-muted mt-0.5 truncate">
                      {n.submission_title}
                    </p>
                  )}
                  <time className="text-[11px] text-text-muted">
                    {timeAgo(n.created_at)}
                  </time>
                </button>
              ))}
          </div>

          {/* Footer */}
          <Link
            to="/notifications"
            onClick={() => setOpen(false)}
            className="block border-t border-border px-4 py-2.5 text-center text-xs font-medium text-primary hover:bg-bg-surface transition-colors"
          >
            View all notifications
          </Link>
        </div>
      )}

      <ConfirmDialog
        open={confirmOpen}
        title="Mark all as read?"
        message="This will mark all notifications as read. This cannot be undone."
        confirmLabel="Mark all read"
        onConfirm={() => void handleMarkAll()}
        onCancel={() => setConfirmOpen(false)}
      />
    </div>
  );
}
